import * as Excel from "exceljs";
import {
  DrinkPayoutSummary,
  TopSellersData,
  TopSellersSummary,
  TransactionsSummary
} from "../menu/drink/menu.drink.analytics";
import { HttpClient } from "@angular/common/http";
import * as fs from "file-saver";
import { MenuOrderData, Order } from "../order/order.model";
import * as moment from "moment";
import { ProductType } from "../product/enums/product-type.enum";

export class ExportService {}

export abstract class ExcelBuilder {
  /**
   * Currency Formatter
   */
  public c = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  /**
   * Colors
   **/

  /**
   * Blues: 0:Dark - 4:Light
   */
  protected b0: Partial<Excel.Color> = {
    argb: "FF2A3962" // Dark Blue
  };

  protected b1: Partial<Excel.Color> = {
    argb: "FF2A3962"
  };

  protected b2: Partial<Excel.Color> = {
    argb: "FF7FC1FF"
  };

  protected b3: Partial<Excel.Color> = {
    argb: "FFBFE0FF"
  };

  protected b4: Partial<Excel.Color> = {
    argb: "FFE6F3FF"
  };

  /**
   * Grays
   */
  protected g1: Partial<Excel.Color> = {
    argb: "FFCACED8" // Gray
  };

  protected g2: Partial<Excel.Color> = {
    argb: "FFE9EBEF" // Half Gray
  };

  /**
   * Oranges
   */
  protected o1: Partial<Excel.Color> = {
    argb: "FFFC7C1B" // Orange
  };

  protected o2: Partial<Excel.Color> = {
    argb: "FFFEDEC6" // Header Orange
  };

  protected o3: Partial<Excel.Color> = {
    argb: "FFFFF2E8" // Data Orange
  };

  /**
   * Fonts
   **/
  protected h1: Partial<Excel.Font> = {
    name: "Karla",
    size: 16,
    bold: true,
    color: this.b0
  };

  protected h2: Partial<Excel.Font> = {
    name: "Karla",
    size: 12,
    color: this.b0
  };

  protected h3: Partial<Excel.Font> = {
    name: "Karla",
    size: 11,
    bold: true,
    color: this.b0
  };

  protected body: Partial<Excel.Font> = {
    name: "Karla",
    size: 11,
    color: this.b0
  };

  /**
   * Alignments
   **/
  protected am: Partial<Excel.Alignment> = {
    vertical: "middle"
  };

  protected amr: Partial<Excel.Alignment> = {
    vertical: "middle",
    horizontal: "right"
  };

  /**
   * Fills
   */
  protected fb0: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.b0
  };
  protected fb1: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.b1
  };
  protected fb2: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.b2
  };
  protected fb3: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.b3
  };
  protected fb4: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.b4
  };
  protected fg1: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.g1
  };
  protected fg2: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.g2
  };
  protected fo1: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.o1
  };
  protected fo2: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.o2
  };

  protected fo3: Partial<Excel.Fill> = {
    type: "pattern",
    pattern: "solid",
    fgColor: this.o3
  };

  /**
   * Borders
   */

  protected white: Partial<Excel.Border> = {
    style: "thin",
    color: { argb: "FFFFFFFF" }
  };

  /*****************************************************************************************
   * Begin Build
   ****************************************************************************************/
  protected workbook: Excel.Workbook;

  constructor() {
    this.workbook = new Excel.Workbook();
  }
}

export class VenueTopSellersSummaryBuilder {
  private file: string = "assets/excel/vTopSellersTemplate.xlsx";
  private wb: Excel.Workbook;

  private get ws(): Excel.Worksheet {
    return this.wb.worksheets[0];
  }

  private get cVenue(): Excel.Cell {
    return this.ws.getCell("D5");
  }
  private get cTimeframe(): Excel.Cell {
    return this.ws.getCell("D6");
  }

  private get firstRow(): number {
    return 10;
  }

  private get cID(): string {
    return "D";
  }
  private get cDRINK(): string {
    return "E";
  }
  private get cCATEGORY(): string {
    return "F";
  }
  private get cUNITS(): string {
    return "G";
  }
  private get cAVG(): string {
    return "H";
  }
  private get cTOTAL(): string {
    return "I";
  }

  constructor(private summary: TopSellersSummary, private http: HttpClient) {}

  async start() {
    await this.loadWorkbookTemplate();
    this.setVenueName();
    this.setTimeframe();
    this.process();
    await this.saveWorkbook();
  }
  private async loadWorkbookTemplate() {
    let buffer: ArrayBuffer = await this.http
      .get(this.file, {
        responseType: "arraybuffer"
      })
      .toPromise();
    this.wb = await new Excel.Workbook().xlsx.load(buffer);
    return;
  }

  private setVenueName() {
    this.cVenue.value = this.summary.venue;
  }

  private setTimeframe() {
    this.cTimeframe.value = this.summary.timeframe;
  }

  private process() {
    this.ws.duplicateRow(
      this.firstRow,
      this.summary.topSellers.length - 1,
      true
    );
    for (var i = 0; i < this.summary.topSellers.length; i++) {
      this.buildRow(
        this.ws.getRow(this.firstRow + i),
        this.summary.topSellers[i],
        i
      );
    }
  }

  private buildRow(row: Excel.Row, data: TopSellersData, index: number) {
    row.getCell(this.cID).value = index + 1;
    row.getCell(this.cDRINK).value = data.name;
    row.getCell(this.cCATEGORY).value = data.categories;
    row.getCell(this.cUNITS).value = data.units;
    row.getCell(this.cTOTAL).value = data.total;
    row.getCell(this.cAVG).value = data.total / data.units;
  }

  private async saveWorkbook() {
    this.wb.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      fs.saveAs(blob, "TopSellersSummary" + ".xlsx");
    });
  }
}

export class AdminVenuePayoutSummaryBuilder {
  private file: string;
  private wb: Excel.Workbook;

  private get cTimeframe(): Excel.Cell {
    return this.ws.getCell("D5");
  }
  constructor(
    private ordersByVenue: Map<string, Order>[],
    private timeframe: string,
    private http: HttpClient
  ) {
    this.setFileLocation();
  }

  async start() {
    await this.loadWorkbookTemplate();
    this.setTimeframe();
    this.process();
    await this.saveWorkbook();
  }
  private setTimeframe() {
    this.cTimeframe.value = this.timeframe;
  }

  private get ws(): Excel.Worksheet {
    return this.wb.worksheets[0];
  }

  private get firstRow(): number {
    return 11;
  }

  private get totalsRow(): Excel.Row {
    return this.ws.getRow(this.firstRow + this.ordersByVenue.length + 1);
  }
  private setFileLocation() {
    this.file = "assets/excel/aVenuePayoutsTemplate.xlsx";
  }

  public get columnsToTotal(): string[] {
    return [
      this.cGROSS,
      this.cGROSSCOVER,
      this.cGROSSDRINK,
      this.cGROSSLS,
      this.cGROSSMENU,
      this.cGROSSOTHER,
      this.cNET,
      this.cNETCOVER,
      this.cNETDRINK,
      this.cNETLS,
      this.cNETMENU,
      this.cNETOTHER,
      this.cFEES,
      this.cFEESCOVER,
      this.cFEESDRINK,
      this.cFEESLS,
      this.cFEESMENU,
      this.cFEESOTHER
    ];
  }
  private process() {
    this.ws.duplicateRow(this.firstRow, this.ordersByVenue.length - 1, true);
    for (var i = 0; i < this.ordersByVenue.length; i++) {
      let row = this.buildRow(
        this.ws.getRow(this.firstRow + i),
        this.ordersByVenue[i]
      );

      //HANDLE TOTALS ROW
      this.columnsToTotal.forEach(col =>
        this.addToCell(
          this.totalsRow.getCell(col),
          <number>row.getCell(col).value
        )
      );
    }
  }

  private get cVENUE(): string {
    return "D";
  }
  private get cCITY(): string {
    return "E";
  }
  private get cGROSSMENU(): string {
    return "G";
  }
  private get cFEESMENU(): string {
    return "H";
  }
  private get cNETMENU(): string {
    return "I";
  }
  private get cGROSSDRINK(): string {
    return "K";
  }
  private get cFEESDRINK(): string {
    return "L";
  }
  private get cNETDRINK(): string {
    return "M";
  }
  private get cGROSSLS(): string {
    return "O";
  }
  private get cFEESLS(): string {
    return "P";
  }
  private get cNETLS(): string {
    return "Q";
  }
  private get cGROSSCOVER(): string {
    return "S";
  }
  private get cFEESCOVER(): string {
    return "T";
  }
  private get cNETCOVER(): string {
    return "U";
  }
  private get cGROSSOTHER(): string {
    return "W";
  }
  private get cFEESOTHER(): string {
    return "X";
  }
  private get cNETOTHER(): string {
    return "Y";
  }
  private get cGROSS(): string {
    return "AA";
  }
  private get cFEES(): string {
    return "AB";
  }
  private get cNET(): string {
    return "AC";
  }

  private addToCell(cell: Excel.Cell, numberToAdd: number) {
    cell.value = <number>cell.value + numberToAdd;
  }
  private addToRow(row: Excel.Row, data: Order): Excel.Row {
    //Section 1
    row.getCell(this.cVENUE).value = data.venue.name;
    row.getCell(this.cCITY).value = data.city.name + ", " + data.city.state;

    switch (data.product.type) {
      case ProductType.Menu:
        this.addToCell(row.getCell(this.cGROSSMENU), data.summary.total);
        this.addToCell(row.getCell(this.cFEESMENU), data.fee);
        this.addToCell(
          row.getCell(this.cNETMENU),
          data.summary.total - data.fee
        );
        break;
      case ProductType.Drink:
        this.addToCell(row.getCell(this.cGROSSDRINK), data.summary.total);
        this.addToCell(row.getCell(this.cFEESDRINK), data.fee);
        this.addToCell(
          row.getCell(this.cNETDRINK),
          data.summary.total - data.fee
        );
        break;
      case ProductType.LineSkip:
        this.addToCell(row.getCell(this.cGROSSLS), data.summary.total);
        this.addToCell(row.getCell(this.cFEESLS), data.fee);
        this.addToCell(row.getCell(this.cNETLS), data.summary.total - data.fee);
        break;
      case ProductType.Cover:
        this.addToCell(row.getCell(this.cGROSSCOVER), data.summary.total);
        this.addToCell(row.getCell(this.cFEESCOVER), data.fee);
        this.addToCell(
          row.getCell(this.cNETCOVER),
          data.summary.total - data.fee
        );
        break;
      case ProductType.Other:
        this.addToCell(row.getCell(this.cGROSSOTHER), data.summary.total);
        this.addToCell(row.getCell(this.cFEESOTHER), data.fee);
        this.addToCell(
          row.getCell(this.cNETOTHER),
          data.summary.total - data.fee
        );
        break;
      default:
        break;
    }
    this.addToCell(row.getCell(this.cGROSS), data.summary.total);
    this.addToCell(row.getCell(this.cFEES), data.fee);
    this.addToCell(row.getCell(this.cNET), data.summary.total - data.fee);
    return row;
  }

  private buildRow(row: Excel.Row, data: Map<string, Order>): Excel.Row {
    data.forEach((order, venueName) => this.addToRow(row, order));
    return row;
  }

  private async loadWorkbookTemplate() {
    let buffer: ArrayBuffer = await this.http
      .get(this.file, {
        responseType: "arraybuffer"
      })
      .toPromise();
    this.wb = await new Excel.Workbook().xlsx.load(buffer);
    return;
  }

  private async saveWorkbook() {
    this.wb.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      fs.saveAs(blob, "AdminVenuePayoutSummary" + ".xlsx");
    });
  }
}

export class AdminDrinkPayoutSummaryBuilder {
  private file: string;
  private wb: Excel.Workbook;

  private get cTimeframe(): Excel.Cell {
    return this.ws.getCell("D5");
  }
  constructor(
    private ordersByVenue: Map<string, Order>[],
    private timeframe: string,
    private http: HttpClient
  ) {
    this.setFileLocation();
  }

  async start() {
    await this.loadWorkbookTemplate();
    this.setTimeframe();
    this.process();
    await this.saveWorkbook();
  }
  private setTimeframe() {
    this.cTimeframe.value = this.timeframe;
  }

  private get ws(): Excel.Worksheet {
    return this.wb.worksheets[0];
  }

  private get firstRow(): number {
    return 11;
  }

  private get totalsRow(): Excel.Row {
    return this.ws.getRow(this.firstRow + this.ordersByVenue.length + 1);
  }
  private setFileLocation() {
    this.file = "assets/excel/aDrinkPayoutsTemplate.xlsx";
  }

  public get columnsToTotal(): string[] {
    return [
      this.cGROSSDRINK,
      this.cGROSSTIPS,
      this.cGROSS,
      this.cTIPFEES,
      this.cDRINKFEES,
      this.cNETDRINK,
      this.cNET,
      this.cNETTIPS
    ];
  }
  private process() {
    this.ws.duplicateRow(this.firstRow, this.ordersByVenue.length - 1, true);
    for (var i = 0; i < this.ordersByVenue.length; i++) {
      let row = this.buildRow(
        this.ws.getRow(this.firstRow + i),
        this.ordersByVenue[i]
      );

      //HANDLE TOTALS ROW
      this.columnsToTotal.forEach(col =>
        this.addToCell(
          this.totalsRow.getCell(col),
          <number>row.getCell(col).value
        )
      );
    }
  }

  private addToCell(cell: Excel.Cell, numberToAdd: number) {
    cell.value = <number>cell.value + numberToAdd;
  }

  private get cVENUE(): string {
    return "D";
  }
  private get cCITY(): string {
    return "E";
  }
  private get cORDERS(): string {
    return "F";
  }
  private get cDRINKS(): string {
    return "G";
  }

  private get cGROSSDRINK(): string {
    return "I";
  }
  private get cDRINKFEES(): string {
    return "J";
  }
  private get cNETDRINK(): string {
    return "K";
  }

  private get cGROSSTIPS(): string {
    return "M";
  }
  private get cTIPFEES(): string {
    return "N";
  }
  private get cNETTIPS(): string {
    return "O";
  }

  private get cGROSS(): string {
    return "Q";
  }
  private get cFEE(): string {
    return "R";
  }
  private get cNET(): string {
    return "S";
  }

  private addToRow(row: Excel.Row, data: Order): Excel.Row {
    //Section 1
    row.getCell(this.cVENUE).value = data.venue.name;
    row.getCell(this.cCITY).value = data.city.name + ", " + data.city.state;
    this.addToCell(row.getCell(this.cORDERS), 1);
    this.addToCell(row.getCell(this.cDRINKS), data.summary.units);

    //Section 2: Drinks
    this.addToCell(row.getCell(this.cGROSSDRINK), data.summary.subtotal);
    this.addToCell(
      row.getCell(this.cDRINKFEES),
      data.summary.tip == 0 ? data.fee : data.fee / 2
    );
    this.addToCell(
      row.getCell(this.cNETDRINK),
      <number>row.getCell(this.cGROSSDRINK).value -
        <number>row.getCell(this.cDRINKFEES).value
    );

    //Section 3: Tips
    this.addToCell(row.getCell(this.cGROSSTIPS), data.summary.tip);
    this.addToCell(
      row.getCell(this.cTIPFEES),
      data.summary.tip == 0 ? 0 : data.fee / 2
    );
    this.addToCell(
      row.getCell(this.cNETTIPS),
      <number>row.getCell(this.cGROSSTIPS).value -
        <number>row.getCell(this.cTIPFEES).value
    );

    //Section 4: Total
    this.addToCell(
      row.getCell(this.cGROSS),
      data.summary.total - data.summary.serviceFee
    );
    this.addToCell(row.getCell(this.cFEE), data.fee);
    this.addToCell(
      row.getCell(this.cNET),
      <number>row.getCell(this.cGROSS).value -
        <number>row.getCell(this.cFEE).value
    );
    return row;
  }

  private buildRow(row: Excel.Row, data: Map<string, Order>): Excel.Row {
    data.forEach((order, venueName) => this.addToRow(row, order));
    return row;
  }

  private async loadWorkbookTemplate() {
    let buffer: ArrayBuffer = await this.http
      .get(this.file, {
        responseType: "arraybuffer"
      })
      .toPromise();
    this.wb = await new Excel.Workbook().xlsx.load(buffer);
    return;
  }

  private async saveWorkbook() {
    this.wb.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      fs.saveAs(blob, "AdminDrinkPayoutSummary" + ".xlsx");
    });
  }
}

export class VenueDrinkTransactionsSummaryBuilder {
  private file: string;
  private wb: Excel.Workbook;

  constructor(private summary: TransactionsSummary, private http: HttpClient) {
    this.setFileLocation();
  }

  private setVenueName() {
    this.cVenue.value = this.summary.venue;
  }

  private setTimeframe() {
    this.cTimeframe.value = this.summary.timeframe;
  }
  async start() {
    await this.loadWorkbookTemplate();
    this.setVenueName();
    this.setTimeframe();
    this.process();
    await this.saveWorkbook();
  }
  private get ws(): Excel.Worksheet {
    return this.wb.worksheets[0];
  }

  private get cVenue(): Excel.Cell {
    return this.ws.getCell("D5");
  }

  private get cTimeframe(): Excel.Cell {
    return this.ws.getCell("D6");
  }
  private get firstRow(): number {
    return 11;
  }
  private get totalsRow(): Excel.Row {
    return this.ws.getRow(this.firstRow + this.summary.orders.length + 1);
  }
  private setFileLocation() {
    this.file = "assets/excel/vTransactionsTemplate.xlsx";
  }
  private process() {
    let totalNetDrink = 0;
    let totalNetTip = 0;
    let totalNet = 0;
    this.ws.duplicateRow(this.firstRow, this.summary.orders.length - 1, true);
    for (var i = 0; i < this.summary.orders.length; i++) {
      let row = this.buildRow(
        this.ws.getRow(this.firstRow + i),
        this.summary.orders[i],
        i
      );
      totalNetDrink += <number>row.getCell(this.cNETDRINK).value;
      totalNetTip += <number>row.getCell(this.cNETTIPS).value;
      totalNet += <number>row.getCell(this.cNET).value;
    }

    this.totalsRow.getCell(this.cNETDRINK).value = totalNetDrink;
    this.totalsRow.getCell(this.cNETTIPS).value = totalNetTip;
    this.totalsRow.getCell(this.cNET).value = totalNet;
  }

  private get cID(): string {
    return "F";
  }
  private get cREDEEM(): string {
    return "E";
  }
  private get cPURCHASE(): string {
    return "D";
  }
  private get cCUSTOMER(): string {
    return "G";
  }
  private get cUNITS(): string {
    return "H";
  }

  private get cGROSSDRINK(): string {
    return "J";
  }
  private get cDRINKFEES(): string {
    return "K";
  }
  private get cNETDRINK(): string {
    return "L";
  }

  private get cGROSSTIPS(): string {
    return "N";
  }
  private get cTIPFEES(): string {
    return "O";
  }
  private get cNETTIPS(): string {
    return "P";
  }

  private get cGROSS(): string {
    return "R";
  }
  private get cFEE(): string {
    return "S";
  }
  private get cNET(): string {
    return "T";
  }
  private buildRow(row: Excel.Row, data: Order, index: number): Excel.Row {
    row.getCell(this.cPURCHASE).value =
      moment(data.summary.purchasedAt).format("L") +
      " " +
      moment(data.summary.purchasedAt).format("LT");

    row.getCell(this.cREDEEM).value = data.summary.redeemedAt
      ? moment(data.summary.redeemedAt).format("L") +
        " " +
        moment(data.summary.redeemedAt).format("LT")
      : "N/A";
    row.getCell(this.cID).value = data.reference.id;
    row.getCell(this.cCUSTOMER).value = data.user.name;
    row.getCell(this.cUNITS).value = data.summary.units;

    row.getCell(this.cGROSSDRINK).value = data.summary.subtotal;
    row.getCell(this.cDRINKFEES).value = data.summary.subtotal * 0.029 + 0.3;
    row.getCell(this.cNETDRINK).value =
      data.summary.subtotal - (data.summary.subtotal * 0.029 + 0.3);

    row.getCell(this.cGROSSTIPS).value = data.summary.tip;
    row.getCell(this.cTIPFEES).value = data.summary.tip * 0.029;
    row.getCell(this.cNETTIPS).value =
      data.summary.tip - data.summary.tip * 0.029;

    row.getCell(this.cGROSS).value = data.summary.subtotal + data.summary.tip;

    row.getCell(this.cFEE).value =
      (data.summary.subtotal + data.summary.tip) * 0.029 + 0.3;

    row.getCell(this.cNET).value =
      <number>row.getCell(this.cGROSS).value -
      <number>row.getCell(this.cFEE).value;

    return row;
  }
  private async loadWorkbookTemplate() {
    let buffer: ArrayBuffer = await this.http
      .get(this.file, {
        responseType: "arraybuffer"
      })
      .toPromise();
    this.wb = await new Excel.Workbook().xlsx.load(buffer);
    return;
  }

  private async saveWorkbook() {
    this.wb.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      fs.saveAs(blob, "DrinkTransactionsSummary" + ".xlsx");
    });
  }
}

export class VenueDrinkPayoutSummaryBuilder {
  private file: string;
  private wb: Excel.Workbook;
  constructor(private summary: DrinkPayoutSummary, private http: HttpClient) {
    this.setFileLocation();
  }
  private get ws(): Excel.Worksheet {
    return this.wb.worksheets[0];
  }
  private get cVenue(): Excel.Cell {
    return this.ws.getCell("D5");
  }
  private get cTimeframe(): Excel.Cell {
    return this.ws.getCell("D6");
  }
  private get cGrossRevenue(): Excel.Cell {
    return this.ws.getCell("G12");
  }
  private get cRevenueFees(): Excel.Cell {
    return this.ws.getCell("G13");
  }
  private get cGrossTips(): Excel.Cell {
    return this.ws.getCell("G17");
  }
  private get cTipFees(): Excel.Cell {
    return this.ws.getCell("G18");
  }

  public async start() {
    await this.loadWorkbookTemplate();
    this.setVenueName();
    this.setTimeframe();
    this.setGrossRevenue();
    this.setRevenueFees();
    this.setGrossTips();
    this.setTipFees();
    this.processTipDistribution();
    this.saveWorkbook();
  }

  private async loadWorkbookTemplate() {
    let buffer: ArrayBuffer = await this.http
      .get(this.file, {
        responseType: "arraybuffer"
      })
      .toPromise();
    this.wb = await new Excel.Workbook().xlsx.load(buffer);
    return;
  }

  private setFileLocation() {
    this.file = "assets/excel/vPayoutsTemplate.xlsx";
    // console.log("Template: ", this.file);
  }
  private get firstRow(): number {
    return 22;
  }
  private async saveWorkbook() {
    this.wb.xlsx.writeBuffer().then(data => {
      let blob = new Blob([data], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      fs.saveAs(blob, "DrinkPayoutSummary" + ".xlsx");
    });
  }

  setVenueName() {
    this.cVenue.value = this.summary.venue;
  }
  setTimeframe() {
    this.cTimeframe.value = this.summary.timeframe;
  }
  setGrossRevenue() {
    this.cGrossRevenue.value = this.summary.grossRevenue;
  }
  setRevenueFees() {
    this.cRevenueFees.value = this.summary.revenueFees;
  }
  setGrossTips() {
    this.cGrossTips.value = this.summary.grossTips;
  }
  setTipFees() {
    this.cTipFees.value = this.summary.tipFees;
  }
  public getSalesCell(row: number): Excel.Cell {
    return this.ws.getRow(row).getCell("E");
  }
  public getRecipientCell(row: number): Excel.Cell {
    return this.ws.getRow(row).getCell("D");
  }
  public getTipCell(row: number): Excel.Cell {
    return this.ws.getRow(row).getCell("F");
  }
  public getPercentageCell(row: number): Excel.Cell {
    return this.ws.getRow(row).getCell("G");
  }
  processTipDistribution() {
    if (this.summary.tipRecipients.length == 0) this.ws.spliceRows(21,3);
    else {
      this.ws.duplicateRow(22, this.summary.tipRecipients.length, true);
      let sales = 0;
      let tips = 0;
      for (var i = 0; i < this.summary.tipRecipients.length; i++) {
        let row = this.firstRow + i;
        this.getRecipientCell(row).style.font.bold = false;
        this.getRecipientCell(row).value = this.summary.tipRecipients[i].name;
        this.getSalesCell(row).value = this.summary.tipRecipients[i].subtotal;
        this.getTipCell(row).value = this.summary.tipRecipients[i].tips;
        sales += this.summary.tipRecipients[i].subtotal;
        tips += this.summary.tipRecipients[i].tips;
        this.getPercentageCell(row).value = this.summary.tipRecipients[
          i
        ].tipPercentage;
      }
      let totalRow = this.ws.getRow(22 + this.summary.tipRecipients.length);
      totalRow.getCell("E").value = sales;
      totalRow.getCell("F").value = tips;
      totalRow.getCell("G").value = tips / sales;
    }
  }
}
